import System from '../System';
import { LuckyTurnipsUI } from 'Prototype/LuckyTurnips/LuckyTurnipsUI';
import StateManager from 'State/StateManager';
import { TGameArea } from 'Utility/Tracking/DataSet/Transaction';
import Game from 'Game';
import { IGameState } from 'State/Game/IGameState';
import { LuckyTurnipsActions } from 'State/LuckyTurnips/LuckyTurnipsActions';
import { ILuckyTurnipsState } from 'State/LuckyTurnips/ILuckyTurnipsState';
import { PlayMusic } from '../Audio/AudioHelper';
import GameObject from 'GameObject/GameObject';
import { STAR_CHEST_TIER_DATA } from 'Constants/Chests';
import { cached } from 'Utility/async';
import { track } from 'Utility/Tracking/Track';
import { getCurrentLevelNumber } from 'State/Player/PlayerLevelHelper';
import { GameActions } from 'State/Game/GameActions';
import { preload as preloadWorldSystem } from '../World/WorldSystemHelper';
import { AUDIO_CLIP_AMBIENT, AUDIO_CLIP_MUSIC, queueLuckyTurnipsResources, } from './LuckyTurnipsSystemHelper';
import AnimationClip from '../Animation/AnimationClip';
import { PlayAnimation } from '../Animation/AnimationHelper';

// TODO: consider using the BaseUIHandler (and turning this into LuckyTurnupsUISystem)
export default class LuckyTurnipsSystem extends System {
    private luckyTurnipsUI: LuckyTurnipsUI | undefined;
    private pickedTurnipIndices = new Array<number>();

    constructor(state: StateManager) {
        super(state);
        track('tGameArea', TGameArea.LUCKY_TURNIPS);
    }

    public init(): void { }

    public async preload() {
        console.log('Lucky Turnips System beginning to preload');

        queueLuckyTurnipsResources();

        await Game.resource.load();

        console.log('Lucky Turnips System done preloading');
    }

    public onStart(): void {
        const state = Game.store.getState();

        preloadWorldSystem(state.playerState.maxWorld);

        const luckyTurnipsState = state.playerState.luckyTurnipsState!;

        track('tStarChestType', luckyTurnipsState.starChestTier + 1);

        this.pickedTurnipIndices = luckyTurnipsState.pickedLootIndices.map(
            lootIndex => {
                return lootIndexToTurnipIndex(luckyTurnipsState, lootIndex);
            }
        );

        if (this.pickedTurnipIndices.length > 0) {
            // TODO: display special message
        }

        this.luckyTurnipsUI = GameObject.Instantiate(LuckyTurnipsUI, {
            starChestTier: luckyTurnipsState.starChestTier,
            lootOrder: luckyTurnipsState.lootOrder,
            picksAllowed:
                STAR_CHEST_TIER_DATA[luckyTurnipsState.starChestTier]
                    .luckyTurnipPicks.length,
            pickedTurnipIndices: this.pickedTurnipIndices,
            playAnimation: (clip: AnimationClip<unknown>) => {
                const deferredRead = this.taskQueue.queue({
                    execute: () => PlayAnimation(clip),
                });
                this.taskQueue.start(this);
                return deferredRead;
            },
            highestUnlockedLevel: getCurrentLevelNumber(state.playerState),
        });
        this.scene.addGameObject(this.luckyTurnipsUI);

        this.luckyTurnipsUI.onTurnipClick((index) => {
            LuckyTurnipsActions.handleTurnipClick(index);
        });

        this.luckyTurnipsUI.onClaimClick(() => {
            LuckyTurnipsActions.handleClaimClick();
        });

        PlayMusic(AUDIO_CLIP_AMBIENT);
        PlayMusic(AUDIO_CLIP_MUSIC);
    }

    protected onStop(): void { }

    protected onDestroy(): void {
        track('tStarChestType', undefined);
    }

    protected onUpdate(): void { }

    protected onLateUpdate(): void { }

    public handleStateChanged(state: IGameState) {
        const luckyTurnipsState = state.playerState.luckyTurnipsState;

        if (luckyTurnipsState) {
            this.checkAnimateTurnipPick(luckyTurnipsState);
            this.checkRevealAll(luckyTurnipsState);
        }

        if (!luckyTurnipsState) this.claimRewardsOnce();
    }

    private checkAnimateTurnipPick(luckyTurnipsState: ILuckyTurnipsState) {
        // check if there are any turnips to animate collection of
        const newPickedTurnipIndices = luckyTurnipsState.pickedLootIndices
            .map(lootIndex =>
                lootIndexToTurnipIndex(luckyTurnipsState, lootIndex)
            )
            .filter(
                turnipIndex => !this.pickedTurnipIndices.includes(turnipIndex)
            );

        if (newPickedTurnipIndices.length > 0) {
            for (const turnipIndex of newPickedTurnipIndices) {
                this.luckyTurnipsUI.animateTurnipPick(turnipIndex);
            }
            this.pickedTurnipIndices.push(...newPickedTurnipIndices);
        }
    }

    private checkRevealAll(luckyTurnipsState: ILuckyTurnipsState) {
        // check if can reveal "claim" button and the remaining rewards
        const picks =
            STAR_CHEST_TIER_DATA[luckyTurnipsState.starChestTier].luckyTurnipPicks
                .length;
        if (this.pickedTurnipIndices.length >= picks) {
            this.luckyTurnipsUI.revealAll();
        }
    }

    private readonly claimRewardsOnce = cached(() => {
        this.startCoroutine(
            function* (this: LuckyTurnipsSystem) {
                yield* this.luckyTurnipsUI.animateClaimRewards();
                GameActions.loadWorld();
            }.call(this)
        );
    });
}

function lootIndexToTurnipIndex(
    luckyTurnipsState: ILuckyTurnipsState,
    lootIndex: number
): number {
    return luckyTurnipsState.lootOrder.indexOf(lootIndex);
}
